import ConsumerRefService from "@api/consumer/consumerRefService";
import {IBalanceRefReward, IItemRefReward, IPercentRefReward, IRefReward, RefRewardType} from "@interfaces/consumer/refs";
import {useItem} from "@hooks/selectors/user/item.selectors";
import {FC, useState} from "react";
import {Link, useNavigate, useParams} from "react-router";
import {ColumnDef} from "@tanstack/react-table";
import {useRemoteTable} from "@hooks/tableHooks";
import {Card, Form} from "react-bootstrap";
import {Flex, IconButton} from "@components/falcon/common";
import {AdvanceTable, AdvanceTableFooter, AdvanceTableSearchBox} from "@components/falcon/common/tables";
import AdvanceTableProvider from "@components/falcon/common/tables/context";
import {useRole} from "@services/hooks";

const RefRewardInfoCell: FC<{ reward: IRefReward }> = ({reward}) => {
    const balance = (reward as IBalanceRefReward).balance;
    const itemId = (reward as IItemRefReward).itemId;
    const count = (reward as IItemRefReward).count;
    const percent = (reward as IPercentRefReward).percent;
    const minSum = (reward as IPercentRefReward).minSum;
    const maxSum = (reward as IPercentRefReward).maxSum;

    const item = useItem(itemId || '');

    switch (reward.type) {
        case RefRewardType.Balance:
            return <span>{balance} ₽</span>;
        case RefRewardType.Item:
            return <span>{item.name} x{count}</span>;
        case RefRewardType.Percent:
            return <span>{percent}% (мин: {minSum}₽, макс: {maxSum}₽)</span>;
        default:
            return <span>-</span>;
    }
};

const columns: ColumnDef<IRefReward>[] = [
    {
        accessorKey: 'name',
        header: 'Название',
        enableSorting: true,
        meta: {
            headerProps: { className: 'pe-1 text-900' },
            cellProps: { className: 'py-2' }
        },
        cell: ({ row: { original: { id, refId, name } } }) => (
            <Link to={`/consumer/refs/${refId}/rewards/${id}/`}>
                <Flex alignItems="center" className="flex-1">
                    <h5 className="mb-0 fs-10">{name}</h5>
                </Flex>
            </Link>
        )
    },
    {
        accessorKey: 'type',
        header: 'Тип',
        enableSorting: false,
        cell: ({ row: { original: { type } } }) => {
            switch (type) {
                case RefRewardType.Balance:
                    return 'Баланс';
                case RefRewardType.Item:
                    return 'Предмет';
                case RefRewardType.Percent:
                    return 'Процент доната';
                default:
                    return '-';
            }
        },
    },
    {
        accessorKey: 'info',
        header: 'Награда',
        enableSorting: false,
        cell: ({ row: { original } }) => (
            <RefRewardInfoCell reward={original} />
        ),
    },
    {
        accessorKey: 'isActive',
        header: 'Активно',
        enableSorting: false,
        meta: {
            headerProps: { className: 'text-center text-900' },
            cellProps: { className: 'text-center' }
        },
        cell: ({ row: { original: { isActive } } }) => (
            <div className="text-center">
                <Form.Check type="checkbox" defaultChecked={isActive} disabled />
            </div>
        )
    }
];

const RefRewardsList: FC = () => {

    const [api] = useState(() => new ConsumerRefService());
    const navigate = useNavigate();
    const { ref_id } = useParams<{ ref_id: string }>();

    const table = useRemoteTable({
        name: `consumer.refs.${ref_id}.rewards`,
        columns,
        defaultState: {
            sorting: [{ id: 'name', desc: false }]
        }
    }, (model) => api.getRefRewards(ref_id || '', model));

    const canEdit = useRole('consumer.ref.edit');

    return (
        <AdvanceTableProvider table={table}>
            <Card className="mb-3">
                <Card.Header>
                    <Flex className='align-items-center justify-content-between'>
                        <Flex alignItems="center" className="fs-10">
                            <h5 className="fs-9 mb-0 text-nowrap py-2 py-xl-0">Список наград</h5>
                        </Flex>
                        <Flex>
                            {canEdit && (
                                <IconButton
                                    variant="falcon-default"
                                    size="sm"
                                    icon="plus"
                                    transform="shrink-3"
                                    className='me-2'
                                    onClick={() => navigate(`/consumer/refs/${ref_id}/rewards/add/`)}
                                >
                                    <span className="d-none d-sm-inline-block ms-1">Создать</span>
                                </IconButton>
                            )}
                            <Flex><AdvanceTableSearchBox /></Flex>
                        </Flex>
                    </Flex>
                </Card.Header>
                <Card.Body className="p-0">
                    <AdvanceTable
                        headerClassName="bg-200 text-nowrap align-middle"
                        rowClassName="align-middle white-space-nowrap"
                        tableProps={{
                            size: 'sm',
                            striped: true,
                            className: 'fs-10 mb-0 overflow-hidden'
                        }}
                    />
                </Card.Body>
                <Card.Footer>
                    <AdvanceTableFooter
                        rowInfo
                        rowsPerPageSelection
                        paginationButtons
                    />
                </Card.Footer>
            </Card>
        </AdvanceTableProvider>
    )
}

export default RefRewardsList;
